import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  Box,
  Typography,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material'
import { Refresh } from '@mui/icons-material'
import { useUsersManagement } from '../../../hooks/useUsersManagement'
import { useAdminData } from '../../../hooks/useAdminData'
import apiService from '../../../../../services/ApiService'
import SearchBar from '../common/SearchBar'
import LiveUsersTable from '../tables/LiveUsersTable'
import UsersTable from '../tables/UsersTable'
import RoleDialog from '../dialogs/RoleDialog'
import ConfirmDialog from '../dialogs/ConfirmDialog'

const LIVE_USERS_PAGE_SIZE = 10

const UsersTab = ({ showNotification, onCountChange, onUserSelect }) => {
  const {
    loading,
    currentPage,
    totalPages,
    totalItems,
    searchTerm,
    oauthFilter,
    roleFilter,
    setSearchTerm,
    setOauthFilter,
    setRoleFilter,
    loadUsers,
    updateUserStatus,
    updateUserRole,
    getFilteredUsers,
    handleSearch,
    handlePageChange,
  } = useUsersManagement()

  const { setMetrics } = useAdminData()
  const [liveUsers, setLiveUsers] = useState([])
  const [liveUsersLoading, setLiveUsersLoading] = useState(false)
  const [liveUsersRefreshing, setLiveUsersRefreshing] = useState(false)
  const [liveUsersPage, setLiveUsersPage] = useState(1)

  const [roleDialog, setRoleDialog] = useState({ open: false, user: null })
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
  })

  // Track if we've loaded data at least once to prevent resetting count to 0
  const hasLoadedRef = useRef(false)
  const previousLoadingRef = useRef(false)

  const loadLiveUsers = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setLiveUsersRefreshing(true)
      } else {
        setLiveUsersLoading(true)
      }

      const stats = await apiService.getAdminStats()
      setLiveUsers(stats?.liveUsersDetails || [])
    } catch (error) {
      console.error('Failed to load live users:', error)
      if (isRefresh) {
        showNotification('Failed to refresh live users', 'error')
      }
    } finally {
      if (isRefresh) {
        setLiveUsersRefreshing(false)
      } else {
        setLiveUsersLoading(false)
      }
    }
  }, [showNotification])

  useEffect(() => {
    loadUsers()
    loadLiveUsers()
  }, [loadUsers, loadLiveUsers])

  useEffect(() => {
    const liveUsersTotalPages = Math.max(
      1,
      Math.ceil(liveUsers.length / LIVE_USERS_PAGE_SIZE)
    )
    if (liveUsersPage > liveUsersTotalPages) {
      setLiveUsersPage(liveUsersTotalPages)
    }
  }, [liveUsers, liveUsersPage])

  // Track when loading completes (transition from loading=true to loading=false)
  useEffect(() => {
    if (previousLoadingRef.current && !loading) {
      // Loading just completed
      hasLoadedRef.current = true
    }
    previousLoadingRef.current = loading
  }, [loading])

  // Update parent with count (only after data has been loaded at least once)
  useEffect(() => {
    if (onCountChange && hasLoadedRef.current && !loading) {
      onCountChange(totalItems)
    }
  }, [totalItems, loading, onCountChange])

  const handleStatusChange = (userId, isActive) => {
    const action = isActive ? 'deactivate' : 'activate'
    setConfirmDialog({
      open: true,
      title: `Confirm ${action.charAt(0).toUpperCase() + action.slice(1)}`,
      message: `Are you sure you want to ${action} this user?`,
      onConfirm: async () => {
        try {
          await updateUserStatus(userId, isActive, setMetrics)
          showNotification(`User ${action}d successfully`, 'success')
        } catch (error) {
          showNotification(error.message || 'Status change failed', 'error')
        } finally {
          setConfirmDialog({ ...confirmDialog, open: false })
        }
      },
    })
  }

  const handleRoleDialogOpen = (user) => {
    setRoleDialog({ open: true, user })
  }

  const handleRoleChange = async (userId, role, shouldAdd) => {
    try {
      const result = await updateUserRole(
        userId,
        role,
        shouldAdd,
        roleDialog.user,
        setMetrics
      )

      if (result.success) {
        setRoleDialog({
          open: true,
          user: { ...roleDialog.user, roles: result.roles },
        })
        showNotification(
          `${role} role ${shouldAdd ? 'added' : 'removed'} successfully`,
          'success'
        )
      }
    } catch (error) {
      showNotification(error.message || 'Role change failed', 'error')
    }
  }

  const handleSearchSubmit = (value) => {
    handleSearch(value)
  }

  const filteredUsers = getFilteredUsers()
  const liveUsersTotalPages = Math.max(
    1,
    Math.ceil(liveUsers.length / LIVE_USERS_PAGE_SIZE)
  )
  const paginatedLiveUsers = liveUsers.slice(
    (liveUsersPage - 1) * LIVE_USERS_PAGE_SIZE,
    liveUsersPage * LIVE_USERS_PAGE_SIZE
  )

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
            flexWrap: 'wrap',
            mb: 2,
          }}
        >
          <Box>
            <Typography variant='h5' sx={{ fontWeight: 600 }}>
              Live Users ({liveUsers.length})
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Distinct authenticated active users from live sessions (excluded-user-emails list applied).
            </Typography>
          </Box>
          <Button
            variant='outlined'
            size='small'
            startIcon={<Refresh />}
            onClick={() => loadLiveUsers(true)}
            disabled={liveUsersRefreshing}
          >
            {liveUsersRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box>

        <LiveUsersTable
          users={paginatedLiveUsers}
          loading={liveUsersLoading}
          onUserClick={onUserSelect}
        />

        {liveUsersTotalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={liveUsersTotalPages}
              page={liveUsersPage}
              onChange={(event, value) => setLiveUsersPage(value)}
              color='primary'
            />
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography variant='h5' sx={{ fontWeight: 600 }}>
          User Management
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <FormControl size='small' sx={{ minWidth: 150 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={roleFilter}
              label='Role'
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <MenuItem value='all'>All Roles</MenuItem>
              <MenuItem value='ADMIN'>Admin</MenuItem>
              <MenuItem value='USER'>User</MenuItem>
            </Select>
          </FormControl>

          <FormControl size='small' sx={{ minWidth: 150 }}>
            <InputLabel>OAuth Filter</InputLabel>
            <Select
              value={oauthFilter}
              label='OAuth Filter'
              onChange={(e) => setOauthFilter(e.target.value)}
            >
              <MenuItem value='all'>All Users</MenuItem>
              <MenuItem value='google'>Google Only</MenuItem>
              <MenuItem value='linkedin'>LinkedIn Only</MenuItem>
            </Select>
          </FormControl>

          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            onSearch={handleSearchSubmit}
            placeholder='Search users by name or email...'
            sx={{ minWidth: 300 }}
          />
        </Box>
      </Box>

      <UsersTable
        users={filteredUsers}
        loading={loading}
        onStatusChange={handleStatusChange}
        onRoleChange={handleRoleDialogOpen}
        onUserClick={onUserSelect}
      />

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(event, value) => handlePageChange(value)}
            color='primary'
          />
        </Box>
      )}

      <RoleDialog
        open={roleDialog.open}
        user={roleDialog.user}
        onClose={() => setRoleDialog({ open: false, user: null })}
        onRoleChange={handleRoleChange}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        onConfirm={confirmDialog.onConfirm}
      />
    </Box>
  )
}

export default UsersTab
